// frontend/src/components/profile/MyListings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vehiclesApi } from '../../api/vehicles';
import helpers from '../../utils/helpers';
import { FaTrash, FaCheckCircle, FaPlus, FaClock, FaEdit, FaUndo, FaInfoCircle, FaRocket } from 'react-icons/fa';
import BoostModal from '../common/BoostModal'; // <--- NEW IMPORT

function MyListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Boost Modal State
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [boostTarget, setBoostTarget] = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const data = await vehiclesApi.getUserListings();
      setListings(data);
    } catch (err) {
      console.error("Error fetching listings:", err);
      setError("Failed to load your listings.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this listing? This cannot be undone.")) {
      try {
        await vehiclesApi.deleteListing(id);
        setListings(listings.filter(item => item.id !== id));
      } catch (err) {
        alert("Failed to delete listing.");
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const action = newStatus === 'sold' ? "Mark as Sold" : "Mark as Available";
    if (window.confirm(`${action}?`)) {
      try {
        await vehiclesApi.updateListing(id, { status: newStatus });
        setListings(listings.map(item => 
          item.id === id ? { ...item, status: newStatus } : item
        ));
      } catch (err) {
        alert("Failed to update status.");
      }
    }
  };

  const handleOpenBoost = (vehicle) => {
      setBoostTarget(vehicle);
      setIsBoostModalOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><FaCheckCircle /> Active</span>;
      case 'pending_review':
        return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><FaClock /> Pending Review</span>;
      case 'sold':
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full flex items-center gap-1"><FaCheckCircle /> Sold</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">{status}</span>;
    }
  };

  if (loading) return <div className="text-center py-10">Loading your listings...</div>;
  if (error) return <div className="text-red-500 py-10">{error}</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl font-bold text-gray-800">My Vehicles</h2>
        <Link 
          to="/vehicles/sell" 
          className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center justify-center gap-2 transition duration-300 w-full sm:w-auto"
        >
          <FaPlus /> Sell New Car
        </Link>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
              <h4 className="font-bold text-blue-800 text-sm">Manage Your Sales</h4>
              <p className="text-sm text-blue-700">
                  Keep your listings updated. Use the <strong>Boost</strong> button to promote your car to the top of the marketplace.
              </p>
          </div>
      </div>

      {listings.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">You haven't listed any vehicles yet.</p>
          <Link to="/vehicles/sell" className="text-primary hover:underline">Start selling today</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map((vehicle) => {
             const mainImage = vehicle.images?.front || Object.values(vehicle.images || {})[0] || 'https://via.placeholder.com/150';
             const isPromoted = vehicle.is_promoted && new Date(vehicle.promoted_until) > new Date();

             return (
              <div key={vehicle.id} className={`bg-white border rounded-lg p-4 flex flex-col md:flex-row gap-4 items-start md:items-center shadow-sm hover:shadow-md transition-shadow ${isPromoted ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'}`}>
                
                <div className="w-full md:w-32 h-32 md:h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative">
                  <img src={mainImage} alt={vehicle.model} className="w-full h-full object-cover" />
                  {isPromoted && (
                      <span className="absolute top-0 left-0 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-br shadow-sm flex items-center gap-1">
                          <FaRocket size={8} /> BOOSTED
                      </span>
                  )}
                </div>

                <div className="flex-grow w-full">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-800 text-lg">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    {getStatusBadge(vehicle.status)}
                  </div>
                  <p className="text-primary font-bold">{helpers.formatCurrency(vehicle.price, 'NGN')}</p>
                  <p className="text-xs text-gray-500 mt-1">Listed on: {new Date(vehicle.created_at).toLocaleDateString()}</p>
                  {isPromoted && (
                      <p className="text-xs text-red-600 font-bold mt-1">
                          Boosted until: {new Date(vehicle.promoted_until).toLocaleString()}
                      </p>
                  )}
                </div>

                <div className="flex flex-wrap md:flex-col gap-2 w-full md:w-auto mt-2 md:mt-0">
                  {/* BOOST BUTTON */}
                  {vehicle.status === 'active' && (
                      <button 
                          onClick={() => handleOpenBoost(vehicle)}
                          className="flex-1 md:flex-none bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition min-w-[80px] shadow-sm"
                      >
                          <FaRocket /> Boost
                      </button>
                  )}

                  <Link 
                    to={`/vehicles/edit/${vehicle.id}`}
                    className="flex-1 md:flex-none bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition min-w-[80px]"
                  >
                    <FaEdit /> Edit
                  </Link>
                  
                  {vehicle.status === 'sold' ? (
                    <button 
                      onClick={() => handleStatusChange(vehicle.id, 'active')}
                      className="flex-1 md:flex-none bg-yellow-50 hover:bg-yellow-100 text-yellow-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition min-w-[80px]"
                    >
                      <FaUndo /> Revert
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleStatusChange(vehicle.id, 'sold')}
                      className="flex-1 md:flex-none bg-green-50 hover:bg-green-100 text-green-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition min-w-[80px]"
                    >
                      <FaCheckCircle /> Sold
                    </button>
                  )}

                  <button 
                    onClick={() => handleDelete(vehicle.id)}
                    className="flex-1 md:flex-none bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition min-w-[80px]"
                  >
                    <FaTrash /> Delete
                  </button>
                </div>

              </div>
             );
          })}
        </div>
      )}

      {/* BOOST MODAL */}
      {isBoostModalOpen && boostTarget && (
          <BoostModal 
              isOpen={isBoostModalOpen}
              onClose={() => setIsBoostModalOpen(false)}
              itemId={boostTarget.id}
              itemType="vehicle"
              itemName={`${boostTarget.year} ${boostTarget.make} ${boostTarget.model}`}
          />
      )}
    </div>
  );
}

export default MyListings;